import { useState, useEffect, useCallback, useRef } from "react";
import { X, Ruler } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared table renderer — used by both SizeGuideModal and FAQ inline
export function SizeGuideTables({ guide }) {
  if (!guide) return null;
  return (
    <div className="space-y-6">
      {guide.tables?.map((table, tIdx) => (
        <div key={tIdx}>
          {table.heading && (
            <>
              {tIdx > 0 && (
                <div className="flex items-center gap-4 my-5">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C4A4A0]/30 to-transparent" />
                </div>
              )}
              <p className="text-[13px] text-[#3D2B1F] font-medium tracking-wide mb-3">{table.heading}</p>
            </>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid={`size-table-${tIdx}`}>
              <thead>
                <tr>
                  {table.columns.map((col, cIdx) => (
                    <th
                      key={cIdx}
                      className={`py-3 px-4 text-[9px] tracking-[0.15em] uppercase font-semibold ${
                        cIdx === 0
                          ? 'text-left text-[#9A7B4F] bg-[#9A7B4F]/[0.06] rounded-tl-md'
                          : 'text-center text-[#8B7355]/60'
                      } ${cIdx === table.columns.length - 1 ? 'rounded-tr-md' : ''} border-b-2 border-[#9A7B4F]/15`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rIdx) => (
                  <tr
                    key={rIdx}
                    className={`transition-colors hover:bg-[#9A7B4F]/[0.04] ${
                      rIdx % 2 === 1 ? 'bg-[#FAF8F0]/50' : ''
                    }`}
                  >
                    {row.map((cell, cIdx) => (
                      <td
                        key={cIdx}
                        className={`py-2.5 px-4 border-b border-[#E8E2D8]/60 ${
                          cIdx === 0
                            ? 'text-left font-semibold text-[#3D2B1F] tracking-wide'
                            : 'text-center text-[#5A5048] font-light tabular-nums'
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {guide.note && (
        <div className="flex gap-2.5 pt-3 mt-2 border-t border-[#E8E2D8]/60">
          <div className="w-0.5 bg-[#C4A4A0]/40 rounded-full flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#8B7355]/80 leading-relaxed font-light">
            {guide.note}
          </p>
        </div>
      )}
    </div>
  );
}

// Hook to load size guide data for a specific category
export function useSizeGuide(categoryId) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (guide) return;
    setLoading(true);
    const params = categoryId ? `?category_id=${categoryId}` : '';
    axios.get(`${API}/settings/size-guide${params}`)
      .then(r => setGuide(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [guide, categoryId]);

  return { guide, loading, load };
}

// Hook to load ALL size guides (for FAQ page)
export function useAllSizeGuides() {
  const [guides, setGuides] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/settings/size-guide`)
      .then(r => setGuides({ general: r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { guides, loading };
}

// Modal component
export default function SizeGuideModal({ open, onClose, categoryId }) {
  const { guide, load } = useSizeGuide(categoryId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      load();
      trackEvent("size_guide_opened");
      // Reset scroll to top when modal opens
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }, 50);
    }
  }, [open, load]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="size-guide-wrapper">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="size-guide-overlay"
      />
      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-[0_25px_60px_rgba(0,0,0,0.15)] w-[95%] max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col z-10"
        data-testid="size-guide-modal"
      >
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-6 pb-4 border-b border-[#E8E2D8]/80 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 text-[#B8A898] hover:text-[#3D2B1F] transition-colors rounded-full hover:bg-[#FAF8F0]"
            data-testid="size-guide-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
              <Ruler className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[9px] text-[#C4A4A0] tracking-[0.25em] uppercase font-medium">Reference</p>
              <h2 className="text-[15px] text-[#3D2B1F] mt-0.5 pr-8">{guide?.title || "Size Guide"}</h2>
            </div>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5" ref={scrollRef}>
          <SizeGuideTables guide={guide} />
        </div>
      </div>
    </div>
  );
}
